/**
 * Runtime helpers that pair with the type exports in ./schemas/*.
 *
 * All type aliases and interfaces are derived from zod schemas via `z.infer`
 * in ./schemas/. This file keeps the non-type bits (the permission check) and
 * re-exports the schema types so `@rapid-response/shared` consumers keep working
 * without needing to know about the layout.
 */

export * from './schemas/index.js'

import { permissionSchema, type Permission } from './schemas/enums.js'

const PERMISSION_SET: ReadonlySet<string> = new Set(
  permissionSchema.options as readonly string[],
)

/**
 * Filter a raw `text[]` (e.g. from `roles.permissions` in Postgres) down to
 * the entries that match the current `Permission` vocabulary. Defends the
 * auth paths against stale strings left by a hand-edit or partial migration —
 * anything unknown is silently dropped rather than propagated to a gate.
 */
export function parsePermissionArray(raw: readonly string[]): Permission[] {
  return raw.filter((p): p is Permission => PERMISSION_SET.has(p))
}

/**
 * Check whether a permission set grants a required permission. The `admin`
 * permission is a master switch — holding it grants every other permission,
 * so callers don't have to list every fine-grained permission on the admin
 * role row.
 */
export function hasPermission(
  permissions: readonly Permission[] | null | undefined,
  required: Permission,
): boolean {
  if (!permissions) return false
  if (permissions.includes('admin')) return true
  return permissions.includes(required)
}
